import { RCS } from 'components/admin/utils/feature-flags';
import { BuilderTypes } from 'components/dashboard/constants';
import { FlowModes, FlowTypes } from 'components/flowBuilder/constants';
import Routes, { flowsRoute } from 'components/flowBuilder/constants/routes';
import {
  FlowTemplateEvents,
  FlowWizardOptions,
} from 'components/flowBuilder/constants/trackingEvents';
import {
  DraftFlow,
  FlowWizardOption,
  SubmitCreateFlowArgs,
} from 'components/flowBuilder/types';
import {
  createDraftFlowWithEndAction,
  createDraftFlowWithInfinityMessageAction,
  createDraftFlowWithRichMessageAction,
  createDraftFlowWithSendMessageAction,
  createFlowCopy,
  ensureFlowActionsReconvene,
} from 'components/flowBuilder/utils/flows';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { cloneDeep, omit } from 'lodash';
import { useHistory } from 'react-router-dom';
import { logEvent } from 'utils/events';

interface HistoryState {
  from?: string;
  flow?: DraftFlow;
}

export const useFlowCreation = (queryString?: string) => {
  const { hasLabsFlag } = usePSLabs();
  const isRCSEnabled = hasLabsFlag(RCS);
  const { push } = useHistory<HistoryState>();

  const loadFlowFromRouter = (
    route: string,
    draftFlow: DraftFlow,
    from?: FlowWizardOption,
  ) => {
    push(route, { flow: draftFlow, from });
  };

  const handleNewTabNavigation = (path: string) => {
    window.open(path, '_blank');
  };

  const openDemoPage = () => {
    handleNewTabNavigation(Routes.BOOK_A_DEMO_AI);
  };

  const createFlow = (args?: SubmitCreateFlowArgs) => {
    // Handle opening the infinity testing marketing page in a new tab
    if (args?.from === FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES) {
      openDemoPage();
      return;
    }

    // Handle closing modal and opening infinity testing learn more flow
    if (args?.from === FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE) {
      // This case needs to be handled by the calling component
      // since it involves modal state management
      return { shouldCloseModal: true, from: args.from };
    }

    // Handle creating a flow (campaign or automation) from a template
    if (args?.template) {
      const { category, data, id, name, type } = args.template;

      logEvent(FlowTemplateEvents.TEMPLATE_SELECTED, {
        template_id: id,
        template_type: type,
        template_name: name,
        template_category: category,
        product_area: 'flow builder - wizard modal',
      });

      const draftFlow: DraftFlow = createFlowCopy(
        omit(cloneDeep(data), 'guid'),
      );

      if (draftFlow) {
        draftFlow.name = `NEW-${name} | ${name}`;
        draftFlow.templateId = id; // We do this to have a record of flows created from templates
        ensureFlowActionsReconvene(draftFlow);
        const path = flowsRoute({
          flowType: draftFlow.type,
          queryString: queryString
            ? `origin=template&${queryString}`
            : 'origin=template',
        });
        loadFlowFromRouter(path, draftFlow, args?.from);
      }
      return;
    }

    // Handle creating a campaign from infinity test campaign
    if (args?.from === FlowWizardOptions.INFINITY_TEST_CAMPAIGN) {
      const draftFlow = createDraftFlowWithInfinityMessageAction({
        mode: FlowModes.ADVANCED,
        type: FlowTypes.CAMPAIGN,
      });
      const path = flowsRoute({
        flowType: draftFlow.type,
        queryString,
      });
      loadFlowFromRouter(path, draftFlow, args?.from);
      return;
    }

    // Handle creating an advanced campaign
    if (args?.from === FlowWizardOptions.CAMPAIGN_FLOW) {
      const draftFlow = createDraftFlowWithEndAction({
        mode: FlowModes.ADVANCED,
        type: FlowTypes.CAMPAIGN,
      });
      const path = flowsRoute({
        flowType: draftFlow.type,
        queryString,
      });
      loadFlowFromRouter(path, draftFlow, args?.from);
      return;
    }

    // Handle creating a basic campaign
    if (args?.from === FlowWizardOptions.BASIC_CAMPAIGN) {
      let draftFlow: DraftFlow;

      if (isRCSEnabled) {
        draftFlow = createDraftFlowWithRichMessageAction({
          mode: FlowModes.BASIC,
          type: FlowTypes.CAMPAIGN,
        });
      } else {
        draftFlow = createDraftFlowWithSendMessageAction({
          mode: FlowModes.BASIC,
          type: FlowTypes.CAMPAIGN,
        });
      }

      const path = flowsRoute({
        flowType: draftFlow.type,
        queryString,
      });
      loadFlowFromRouter(path, draftFlow, args?.from);
    }

    // Handle creating a legacy automation
    if (args?.from === FlowWizardOptions.LEGACY_CAMPAIGN) {
      const path = flowsRoute({
        flowType: FlowTypes.CAMPAIGN,
        builderType: BuilderTypes.LEGACY,
        queryString,
      });
      // Navigate to legacy automation creation page
      push(path);
      return;
    }

    // Handle creating an automation from scratch
    if (args?.from === FlowWizardOptions.AUTOMATION) {
      const draftFlow = createDraftFlowWithEndAction({
        mode: FlowModes.ADVANCED,
        type: FlowTypes.AUTOMATION,
      });
      const path = flowsRoute({
        flowType: draftFlow.type,
        queryString,
      });
      loadFlowFromRouter(path, draftFlow, args?.from);
      return;
    }

    // Handle creating a legacy automation
    if (args?.from === FlowWizardOptions.LEGACY_AUTOMATION) {
      const path = flowsRoute({
        flowType: FlowTypes.AUTOMATION,
        builderType: BuilderTypes.LEGACY,
        queryString,
      });
      // Navigate to legacy automation creation page
      push(path);
      return;
    }

    // Handle creating an automation from a template
    if (
      args?.from === FlowWizardOptions.AUTOMATION_TEMPLATE &&
      args?.template
    ) {
      // This case is already handled above in the template section
      // The template logic will use the correct route based on the flow type
      return null;
    }
  };

  return {
    createFlow,
  };
};
